import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronRight, ChevronDown } from "lucide-react";
import { SERVICE_DATA } from "@/data/services";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/portfolio", label: "Portfolio" },
  { path: "/led-estimator", label: "LED Estimator" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setServicesOpen(false);
  }, [location]);

  const isServicePage = location.pathname.startsWith("/services");

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-10 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "nav-blur border-b border-white/5" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <span><span className="font-heading text-2xl font-bold tracking-tight text-white">VAN</span><span className="font-heading text-2xl font-bold tracking-tight gold-shimmer">SIGN</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          <Link
            to="/"
            data-testid="nav-link-home"
            className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
              location.pathname === "/" ? "text-gold" : "text-white/70 hover:text-white"
            }`}
          >
            Home
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-link-services"
              className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 flex items-center gap-1 ${
                isServicePage ? "text-gold" : "text-white/70 hover:text-white"
              }`}
            >
              Services
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-[#131a23] border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                  data-testid="services-dropdown"
                >
                  <div className="py-2">
                    {SERVICE_DATA.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        data-testid={`dropdown-${service.slug}`}
                        className="flex items-center gap-3 px-5 py-3 text-sm text-white/70 hover:text-gold hover:bg-white/5 transition-all"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-gold/40"></div>
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.slice(1).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-gold"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+17783220801"
            data-testid="nav-phone-btn"
            className="flex items-center gap-2 text-sm text-white/80 hover:text-gold transition-colors"
          >
            <Phone className="w-4 h-4" />
            (778) 322-0801
          </a>
          <Link
            to="/booking"
            data-testid="nav-book-btn"
            className="bg-gold text-black font-bold px-6 py-2.5 rounded-full text-sm hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-0.5"
          >
            Free Consultation
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0f1419] border-t border-white/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4 max-h-[calc(100vh-7.5rem)] overflow-y-auto">
              <Link
                to="/"
                data-testid="mobile-nav-home"
                className={`text-lg font-medium flex items-center justify-between ${
                  location.pathname === "/" ? "text-gold" : "text-white/80"
                }`}
              >
                Home
                <ChevronRight className="w-4 h-4 text-white/30" />
              </Link>

              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  data-testid="mobile-nav-services-toggle"
                  className={`w-full text-lg font-medium flex items-center justify-between ${
                    isServicePage ? "text-gold" : "text-white/80"
                  }`}
                >
                  Services
                  <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pt-3 pb-1 space-y-2">
                        {SERVICE_DATA.map((service) => (
                          <Link
                            key={service.slug}
                            to={`/services/${service.slug}`}
                            data-testid={`mobile-service-${service.slug}`}
                            className={`block text-sm py-1.5 ${
                              location.pathname === `/services/${service.slug}` ? "text-gold" : "text-white/60"
                            }`}
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`text-lg font-medium flex items-center justify-between ${
                    location.pathname === link.path ? "text-gold" : "text-white/80"
                  }`}
                >
                  {link.label}
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </Link>
              ))}

              <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
                <a
                  href="tel:+17783220801"
                  data-testid="mobile-phone-btn"
                  className="flex items-center gap-2 text-white/80"
                >
                  <Phone className="w-4 h-4" /> (778) 322-0801
                </a>
                <Link
                  to="/booking"
                  data-testid="mobile-book-btn"
                  className="bg-gold text-black font-bold px-6 py-3 rounded-full text-center text-sm"
                >
                  Free Consultation
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
